import { z } from 'zod';

import type { TariffDetailLike } from './baggage_allowance.js';

/**
 * AS REGRAS DA TARIFA que o fornecedor declara em `fares.tariffDetails[]` — o que
 * o viajante pergunta antes de cancelar, alterar ou faltar ao voo (#4644). O mesmo
 * array carrega as linhas de bagagem, que já viram `baggageAllowance`; estas são
 * as outras.
 *
 * Cada linha é `"<Regra>, <status>[: <detalhe>]."` na língua da conta: "Alteração
 * de viagem, com cobrança: a Partir de R$ 550.00 / US$ 120 / EUR 120.",
 * "Reembolso, incluído na tarifa: 70,00% da tarifa + taxas de cancelamento.",
 * "Cancelamento de viagem, restrito.". O `name` estruturado diz QUAL regra; a
 * cláusula depois da vírgula diz o status. Nenhuma das duas precisa do léxico de
 * texto livre (`fare_benefit_lexicon`): ele existe para frase sem enum, e aqui o
 * fornecedor já deu os dois.
 *
 * Regra de ouro: a prosa vai VERBATIM em `text` — é sempre mais específica que a
 * nossa leitura. `status` e `amount` só resumem; o que não se lê com certeza fica
 * `unknown`/`null`.
 */
const FareRuleKindSchema = z.enum([
  'CHANGE',
  'CANCELLATION',
  'REFUND',
  'NO_SHOW_RATE',
  'ANTICIPATION',
  'SEATS_MARKING',
  'SEATS_WITH_SPACE_MARKING',
]);
export type FareRuleKind = z.infer<typeof FareRuleKindSchema>;

/** `included` = coberto pela tarifa · `charged` = permitido, com cobrança ·
 *  `restricted` = a tarifa não permite · `unavailable` = o fornecedor não oferece ·
 *  `unknown` = a linha não diz. */
const FareRuleStatusSchema = z.enum([
  'included',
  'charged',
  'restricted',
  'unavailable',
  'unknown',
]);
export type FareRuleStatus = z.infer<typeof FareRuleStatusSchema>;

export const FareRuleSchema = z.object({
  kind: FareRuleKindSchema,
  status: FareRuleStatusSchema,
  /** A prosa do fornecedor, verbatim; `null` quando a linha veio sem ela. */
  text: z.string().nullable(),
  /** O valor em R$ que a prosa escreve com o símbolo — muitas vezes um PISO ("a
   *  partir de"); quem responde lê `text` para saber. `null` sem "R$" explícito:
   *  um número sem moeda nunca vira valor. */
  amount: z.object({ value: z.number(), currency: z.literal('BRL') }).nullable(),
});
export type FareRule = z.infer<typeof FareRuleSchema>;

/** `CANCELLATION_TAX` é a linha de cancelamento das tarifas que não trazem
 *  `CANCELLATION`. Bagagem (`LUGGAGE`/`HAND_LUGGAGE`), `FIDELITY` e `UNKNOWN`
 *  ficam de fora: não são regra de alteração, cancelamento ou assento. */
const KIND_BY_NAME = new Map<string, FareRuleKind>([
  ...FareRuleKindSchema.options.map((kind): [string, FareRuleKind] => [kind, kind]),
  ['CANCELLATION_TAX', 'CANCELLATION'],
]);

/** A cláusula de status nas três línguas que a fonte escreve. */
const STATUS_BY_CLAUSE = new Map<string, FareRuleStatus>([
  ['incluido na tarifa', 'included'],
  ['included in fare', 'included'],
  ['incluido en la tarifa', 'included'],
  ['com cobranca', 'charged'],
  ['with charge', 'charged'],
  ['con cargo', 'charged'],
  ['restrito', 'restricted'],
  ['restricted', 'restricted'],
  ['restringido', 'restricted'],
  ['indisponivel', 'unavailable'],
  ['unavailable', 'unavailable'],
  ['no disponible', 'unavailable'],
]);

/**
 * O status de UMA linha de regra. A prosa decide, mesmo contra `availability`: o
 * reembolso parcial ("Reembolso, incluído na tarifa: 70,00% da tarifa…") vem com
 * `availability: false` e é incluído. Sem cláusula conhecida, só
 * `availability: true` fala — `false` cobre tanto "com cobrança" quanto "restrito".
 */
export function fareRuleStatus(detail: TariffDetailLike): FareRuleStatus {
  const clause = /,([^:.]*)/.exec(detail.description ?? '')?.[1] ?? '';
  const key = clause.trim().toLowerCase().normalize('NFD').replaceAll(/[̀-ͯ]/g, '');
  return STATUS_BY_CLAUSE.get(key) ?? (detail.availability === true ? 'included' : 'unknown');
}

/** "R$ 550.00", "R$ 1.096,56", "R$2,718.00": o ÚLTIMO separador seguido de dois
 *  dígitos é o decimal; os outros são milhar. */
function brlAmount(text: string | null): FareRule['amount'] {
  const raw = /R\$\s*(\d[\d.,]*)/.exec(text ?? '')?.[1]?.replace(/[.,]$/, '');
  if (raw === undefined) return null;
  const decimal = /[.,](\d{2})$/.exec(raw);
  const whole = (decimal ? raw.slice(0, decimal.index) : raw).replaceAll(/[.,]/g, '');
  const cents = decimal?.[1] ?? '00';
  const value = Number(`${whole}.${cents}`);
  return Number.isFinite(value) ? { value, currency: 'BRL' } : null;
}

/** As regras do array, na ordem em que o fornecedor as deu. Vazio quando o array
 *  só tem bagagem (ou nada) — nunca uma regra inventada. */
export function deriveFareRules(
  details: readonly TariffDetailLike[] | null | undefined,
): FareRule[] {
  return (details ?? []).flatMap((detail) => {
    const kind = KIND_BY_NAME.get(detail.name?.trim().toUpperCase() ?? '');
    if (kind === undefined) return [];
    const text = detail.description?.trim() || null;
    return [{ kind, status: fareRuleStatus(detail), text, amount: brlAmount(text) }];
  });
}
